import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";

const SaveAsPaidModal = ({ open, onClose, onSave, loading, totalAmount, invoiceCount }) => {
  const [modeOfPayment, setModeOfPayment] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentType, setPaymentType] = useState("full");

  const handleClose = () => {
    setModeOfPayment("");
    setPaymentAmount("");
    setPaymentType("full");
    onClose();
  };

  const handleSave = () => {
    if (paymentType === "full") {
      onSave(modeOfPayment, null); 
    } else {
      onSave(modeOfPayment, paymentAmount);
    }
  };

  const handleModeOfPaymentChange = (event) => {
    setModeOfPayment(event.target.value);
  };

  const handlePaymentAmountChange = (event) => {
    const value = event.target.value;
    if (totalAmount && (value === "" || (Number(value) > 0 && Number(value) <= totalAmount))) {
      setPaymentAmount(value);
    }
  };

  const handlePaymentTypeChange = (event) => {
    setPaymentType(event.target.value);
    setPaymentAmount(""); // Reset payment amount when switching types
  };

  const isPartialPaymentDisabled = invoiceCount > 1;
  const isAlreadyPaid = totalAmount === 0;

  return (
    <Modal open={open} onClose={handleClose} aria-labelledby="modal-title">
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
          color: "#000000",
        }}
      >
        <Typography variant="h6" id="modal-title" sx={{ color: "#000000" }}>
          Save as Paid
        </Typography>
        {isAlreadyPaid ? (
          <Typography variant="body1" sx={{ mt: 2, color: "#000000" }}>
            This {invoiceCount > 1 ? "invoices are" : "invoice is"} already paid (Remaining Balance: $0.00).
          </Typography>
        ) : (
          <Typography variant="body1" sx={{ mt: 2, color: "#000000" }}>
            {invoiceCount > 1
              ? `Mark ${invoiceCount} invoices as paid (Remaining Balance: $${totalAmount ? totalAmount.toFixed(2) : "Loading..."})`
              : `Enter payment details for the selected invoice (Remaining Balance: $${totalAmount ? totalAmount.toFixed(2) : "Loading..."})`}
          </Typography>
        )}

        {/* Payment Type Selection */}
        {!isAlreadyPaid && (
          <FormControl component="fieldset" sx={{ mt: 2 }}>
            <RadioGroup
              row
              value={paymentType}
              onChange={handlePaymentTypeChange}
              name="payment-type"
            >
              <FormControlLabel
                value="full"
                control={<Radio />}
                label="Full Payment"
                sx={{ color: "#000000" }}
              />
              <FormControlLabel
                value="partial"
                control={<Radio />}
                label="Partial Payment"
                disabled={isPartialPaymentDisabled}
                sx={{ color: isPartialPaymentDisabled ? "#666666" : "#000000" }}
              />
            </RadioGroup>
          </FormControl>
        )}

        {/* Payment Amount Input (only for partial payment, single invoice) */}
        {!isAlreadyPaid && paymentType === "partial" && (
          <TextField
            fullWidth
            margin="normal"
            label="Payment Amount"
            type="number"
            value={paymentAmount}
            onChange={handlePaymentAmountChange}
            inputProps={{ min: 0 }}
            sx={{ color: "#000000" }}
            disabled={!totalAmount}
            helperText={
              isPartialPaymentDisabled
                ? "Partial payment is only available for a single invoice."
                : `Enter an amount up to $${totalAmount ? totalAmount.toFixed(2) : "0.00"}`
            }
          />
        )}

        {/* Mode of Payment Dropdown */}
        {!isAlreadyPaid && (
          <FormControl fullWidth margin="normal">
            <InputLabel sx={{ color: "#000000" }}>Mode of Payment</InputLabel>
            <Select
              value={modeOfPayment}
              onChange={handleModeOfPaymentChange}
              label="Mode of Payment"
              sx={{ color: "#000000" }}
              MenuProps={{
                sx: {
                  "& .MuiPaper-root": {
                    color: "#000000",
                  },
                  "& .MuiMenuItem-root": {
                    color: "#000000",
                  },
                },
              }}
            >
              <MenuItem value="Cheque">Cheque</MenuItem>
              <MenuItem value="Cash">Cash</MenuItem>
              <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
              <MenuItem value="Credit Card">Credit Card</MenuItem>
              <MenuItem value="Bank Remittance">Bank Remittance</MenuItem>
            </Select>
          </FormControl>
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
          <Button variant="contained" onClick={handleClose} color="secondary">
            {isAlreadyPaid ? "Close" : "Cancel"}
          </Button>
          {!isAlreadyPaid && (
            <Button
              variant="contained"
              onClick={handleSave}
              color="primary"
              disabled={
                loading ||
                !modeOfPayment ||
                (paymentType === "partial" && (!paymentAmount || !totalAmount))
              }
            >
              {loading ? <CircularProgress size={24} /> : "Save Payment"}
            </Button>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default SaveAsPaidModal;